/**
 * What: Shopify "App home" (new post-install home page) merchant-facing copy.
 * Transport / data-flow: Pure string dictionary segment merged by shopify-component-copy.ts for Shopify UI consumers.
 * Dynamic banner text (derive.ts) and toast strings (useHome.ts) stay inline where they're built from
 * multiple interpolated, branch-dependent values — only copy renderable as a fixed template lives here.
 */
export const shopifyComponentAppHomeCopy = {
    appHome: {
        heroAriaLabel: 'What TestFeed is',
        heroTitle: 'Test your store on 200 simulated shoppers',
        heroBody: "Show them two versions. See which one they'd buy, and why. All before you spend on ads.",
        heroExampleBadge: 'Example',
        heroReplayAction: 'Replay example',
        heroDismissAction: 'Dismiss introduction',
        startSetupAction: 'Start setup',
        continueSetupAction: 'Continue setup',
        startTestAction: 'Start a test',
        onboardingCallHeading: 'Meet the TestFeed team',
        onboardingCallBody: 'Book an optional onboarding call if you would like help getting started.',
        onboardingCallAction: 'Choose a time',
        onboardingCallExternal: 'Open the calendar in a new tab',
        onboardingCallClose: 'Close',
        primaryNewTestAction: 'New test',
        primaryUpgradeAction: 'Upgrade plan',
        showSetupGuideAction: 'Show setup guide',
        howItWorksAction: 'How it works',
        resyncAction: 'Resync store data',
        resyncingAction: 'Resyncing store data',
        guideHeading: 'Setup guide',
        guideAriaLabel: 'Setup guide',
        guideDismissAction: 'Dismiss setup guide',
        guideExpandAction: 'Expand setup guide',
        guideCollapseAction: 'Collapse setup guide',
        guideProgressAllDone: 'All {total} steps done',
        guideProgressPartial: '{done} of {total} steps done',
        guideAllDoneBody: 'Your shoppers are ready. Run your first test.',
        guideHideAction: 'Hide guide',
        stepConnectTitle: 'Connect your store',
        stepPlanTitle: 'Choose your plan',
        stepSyncTitle: 'Sync your store data',
        statusWordTodo: 'Not started',
        statusWordLocked: 'Waiting on an earlier step',
        statusWordWorking: 'In progress',
        statusWordProblem: 'Needs attention',
        statusWordDone: 'Done',
        connectGrantedNoDataBody: 'Connected. Your store data has not arrived yet.',
        connectGrantedNoCountsBody: 'Your store is connected.',
        connectGrantedBody: '{products} products and {orders} orders connected.',
        connectProblemBody: "Permission wasn't given, so there's nothing to build shoppers from.",
        connectDefaultBody: 'TestFeed reads your products and orders. It never reads names or emails.',
        connectTryAgainAction: 'Try again',
        connectAction: 'Connect store',
        connectWhatWeReadAction: 'What we read',
        planActiveBody: '{name} plan, {testsIncluded} tests a month.',
        planTrialSuffix: ' Free trial, {trialDaysLeft} days left.',
        planCancelledBody: 'Your {previousPlan} plan ended. Pick a plan to keep testing.',
        planDefaultBody: 'Pick how many tests you need each month. Change anytime.',
        planChooseAction: 'Choose plan',
        syncDoneBody: '{shoppers} simulated shoppers built from {orders} orders. Last synced {lastSynced}.',
        syncDoneNoCountsBody: 'Your store data is ready.',
        syncProgressLabel: 'Shopper sync progress',
        syncingBody: "{percent}% done. You can leave this page. We'll keep going.",
        syncingNoProgressBody: "Getting your store data. You can leave this page. We'll keep going.",
        syncLockedBody: 'We build simulated shoppers from your store data. {waitingOn}',
        syncWaitingOnConnect: 'Connect your store first.',
        syncWaitingOnPlan: 'Choose a plan first.',
        syncDefaultBody: 'We build simulated shoppers from your store data. It runs in the background.',
        syncRetryAction: 'Retry sync',
        syncStartAction: 'Start sync',
        capabilitiesHeading: 'What TestFeed does for your store',
        capabilitiesOpensAfterSetup: 'Opens after setup',
        capabilitiesNotLoadedReason: 'Not loaded yet',
        capabilitiesTestsLockedReason: 'Opens after setup',
        capabilitiesTestsLockedCancelledReason: 'Choose a plan to test again',
        capabilitiesInsightsLockedReason: 'Opens after setup',
        capProductsGroup: 'Test',
        capProductsTitle: 'Test products',
        capProductsBody: 'Pit two photos, prices or names against each other.',
        capStoreGroup: 'Test',
        capStoreTitle: 'Test your store',
        capStoreBody: 'Try a page, offer or banner before it goes live.',
        capProductInsightsGroup: 'Learn',
        capProductInsightsTitle: 'Product insights',
        capProductInsightsBody: 'See why shoppers pick a product or pass.',
        capInsightsGroup: 'Learn',
        capInsightsTitle: 'Insights',
        capInsightsBody: 'Meet the shopper types behind your sales.',
        capOpportunitiesGroup: 'Improve',
        capOpportunitiesTitle: 'Opportunities',
        capOpportunitiesBody: 'Fixes ranked by how much they could help, ready to test.',
        capabilitiesLockedFallbackReason: 'Finish setup first',
        recentTestsHeading: 'Recent tests',
        recentTestsUsageLabel: '{used} of {included} used this month',
        recentTestsEmptySetupTitle: 'Your results will show here',
        recentTestsEmptyReadyTitle: 'Run your first test',
        recentTestsEmptySetupBody: 'Finish setup to run your first test.',
        recentTestsEmptyReadyBody: 'Pick two versions and let 200 simulated shoppers choose.',
        recentTestsRunningLabel: 'Running',
        recentTestsDidntFinishLabel: "Didn't finish",
        recentTestsProgressLabel: '{name} progress',
        recentTestsPercentAnsweredLabel: '{percent}% of shoppers answered',
        recentTestsNoResultLabel: 'No result',
        recentTestsSeeAllAction: 'See all tests',
        verdictClear: 'Clear winner',
        verdictLeaning: 'Leaning toward',
        verdictTie: 'Too close to call',
        trustHeading: 'Private by design',
        trustBody: 'Simulated shoppers come from buying patterns. No names, emails or addresses are collected.',
        trustLink: 'How we protect data',
        footerLead: 'Learn more about',
        footerLink: 'how TestFeed works',
        loadErrorHeading: "Couldn't load TestFeed",
        loadErrorUnreachable: "Couldn't reach TestFeed",
        loadErrorSuffix: '. Your tests and shoppers are safe.',
        loadErrorLoadedToastMessage: 'Try again once TestFeed loads',
        skeletonAriaLabel: 'Loading TestFeed',
    },
};
//# sourceMappingURL=app-home.js.map